/**
 * Scene Data to ExportProps Transformer
 *
 * Converts Firebase scene JSON or direct scene data to the ExportProps format
 * used by the HTML generation system.
 */
import { normalizeExtraWaypoints } from '../dynamic-viewer/extraWaypoints';
import type { SceneData, WaypointData, ExtraWaypointData, ExportProps, SplatSwapPoint, HotspotData, PortalData, OverlayData, CustomMeshConfig, HtmlMeshConfig, LightConfig, ParticleConfig, CollisionMeshConfig, AudioEmitterConfig, TemplateType, OrbitCameraSettingsConfig } from '../types';
type InteractionDataWithText = {
    text?: unknown;
};
function getInteractionText(data: unknown): string | undefined {
    if (!data || typeof data !== 'object')
        return undefined;
    const maybe = data as InteractionDataWithText;
    return typeof maybe.text === 'string' ? maybe.text : undefined;
}
/**
 * Transform scene data to ExportProps format
 */
/**
 * The scene's own 8th Wall engine location, kept only as a same-site path (`/cdn/8thwall`,
 * `./cdn/8thwall` for self-hosted exports). The value becomes a <script src>, and scene files are
 * written by their owners, so a URL on another origin would let any creator run script on the
 * site that shows the scene. Hosts that serve the engine elsewhere pass
 * ViewerOptions.eighthWallBaseUrl (or a data-8thwall-base attribute) instead.
 */
export function sameSiteEighthWallBaseUrl(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const url = value.trim();
    return /^\.?\/(?!\/)[A-Za-z0-9._~\-/]*$/.test(url) && !url.includes('..') ? url : undefined;
}

export function transformSceneToExportProps(scene: SceneData): ExportProps {
    // Get all available URLs
    const originalUrl = scene.loadedModelUrl || scene.splatUrl || '';
    const sogUrl = scene.sogModelUrl || scene.sogUrl;
    const compressedPlyUrl = scene.compressedPlyUrl;
    const lodMetaUrl = scene.lodMetaUrl; // LOD streaming format (highest priority)
    // Mobile counterparts — surfaced to ExportProps so the viewer can prefer them on mobile UAs.
    const mobileSplatUrl = scene.mobileLoadedModelUrl || scene.mobileSplatUrl;
    const mobileSogUrl = scene.mobileSogModelUrl || scene.mobileSogUrl;
    const mobileCompressedPlyUrl = scene.mobileCompressedPlyUrl;
    const mobileLodMetaUrl = scene.mobileLodMetaUrl;
    // Skybox: prefer the new nested format (`scene.skybox`), but support legacy fields used by the editor.
    // This ensures older stored scene JSON still renders a skybox in the viewer.
    const legacySkyboxUrl = (typeof scene.activeSkyboxUrl === 'string' && scene.activeSkyboxUrl) ? scene.activeSkyboxUrl :
        (typeof scene.skyboxUrl === 'string' && scene.skyboxUrl) ? scene.skyboxUrl :
            undefined;
    const resolvedSkybox = scene.skybox
        ? { ...scene.skybox, verticalOffset: scene.skybox.verticalOffset ?? scene.skyboxVerticalOffset }
        : (legacySkyboxUrl ? {
            url: legacySkyboxUrl,
            rotation: scene.skyboxRotation,
            verticalOffset: scene.skyboxVerticalOffset
        } : undefined);
    // Prefer LOD > SOG > compressed PLY, while retaining any native original format.
    // Raw .splat still needs conversion; SPZ and KHR_gaussian_splatting GLB/GLTF load natively.
    const originalExt = originalUrl.split('?')[0].split('.').pop()?.toLowerCase();
    const isPlayCanvasCompatible = originalUrl.includes('.compressed.ply') ||
        originalExt === 'ply' || originalExt === 'sog' || originalExt === 'spz' ||
        originalExt === 'glb' || originalExt === 'gltf';
    // Choose the best URL for PlayCanvas (for backward compatibility, splatUrl is the main URL)
    // LOD streaming is handled separately as lodMetaUrl
    let splatUrl = originalUrl;
    if (sogUrl) {
        splatUrl = sogUrl; // SOG is best (after LOD)
    }
    else if (compressedPlyUrl) {
        splatUrl = compressedPlyUrl; // Compressed PLY is second best
    }
    else if (!isPlayCanvasCompatible) {
        // Original is not compatible, keep it but warn
        console.warn('[StorySplat Viewer] Original file format may not be compatible with PlayCanvas:', originalExt);
    }
    const sceneFov = scene.fov != null ? (scene.fov < 3.5 ? scene.fov * (180 / Math.PI) : scene.fov) : undefined;
    // Transform waypoints
    const waypoints = (scene.waypoints || []).map((wp: WaypointData) => {
        // Preserve missing waypoint FOV as "use scene FOV". Older code filled
        // this with 60, which made waypoint data silently override global FOV.
        let fov = wp.fov;
        if (fov != null && fov < 3.5) {
            // FOV is in radians, convert to degrees
            fov = fov * (180 / Math.PI);
        }
        // Extract info from interactions (type: 'info') if not directly on waypoint
        // In SceneTypes.ts, waypoint info is stored in interactions array with type 'info'
        const infoInteraction = wp.interactions?.find((i) => i.type === 'info');
        const interactionHtml = infoInteraction?.data?.textHtml;
        const infoHtml = typeof interactionHtml === 'string' ? interactionHtml : wp.infoHtml;
        let info = typeof interactionHtml === 'string' ? getInteractionText(infoInteraction?.data) || '' : wp.info || wp.description || '';
        if (!info && wp.interactions) {
            const infoInteraction = wp.interactions.find((i) => i.type === 'info');
            const text = getInteractionText(infoInteraction?.data);
            if (text) {
                info = text;
            }
        }
        return {
            position: wp.position || { x: wp.x || 0, y: wp.y || 0, z: wp.z || 0 },
            rotation: wp.rotation || { x: 0, y: 0, z: 0 },
            fov,
            duration: wp.duration || 2000,
            name: wp.name || wp.title || '',
            info,
            ...(infoHtml !== undefined ? { infoHtml } : {}),
            interactions: wp.interactions || [],
            triggerDistance: wp.triggerDistance ?? 1.0,
            curveOut: wp.curveOut,
            // Its id (the visitor directory's view order refers to it).
            ...(typeof wp.id === 'string' && wp.id ? { id: wp.id } : {}),
            // Left out of the waypoints menu only; the tour still visits it.
            ...(wp.hideInMenu === true ? { hideInMenu: true } : {}),
            // Listed under Views in the visitor directory.
            ...(wp.showInDirectory === true ? { showInDirectory: true } : {}),
            // Prev / Next step over it; the camera still passes through.
            ...(wp.skipOnNextPrev === true ? { skipOnNextPrev: true } : {}),
            // Name kept out of the top banner; it still lists the waypoint in the menu.
            ...(wp.hideNameInViewer === true ? { hideNameInViewer: true } : {}),
            // Cutaway change when the tour arrives here.
            ...(wp.cutaway && typeof wp.cutaway === 'object' ? { cutaway: wp.cutaway } : {})
        };
    });
    // Backward-compatible particle fallback:
    // prefer particleSystems only when it actually contains entries, otherwise use legacy particles.
    const resolvedParticles = Array.isArray(scene.particleSystems) && scene.particleSystems.length > 0
        ? scene.particleSystems
        : (scene.particles || []);
    // Editor authors hotspots with `billboardRange: { type, start, end }` (the
    // same nested shape used for `visibilityRange` and for CustomMesh). The
    // viewer's hotspot path reads the flat `billboardRangeStart` /
    // `billboardRangeEnd` fields. Flatten here so creators who set a billboard
    // range in the editor see the same behavior in the published scene.
    const resolvedHotspots = (scene.hotspots || []).map((h: HotspotData) => {
        const editorHotspot = h as HotspotData & {
            billboardRange?: { type?: string; start?: number; end?: number };
        };
        const nested = editorHotspot.billboardRange;
        if (!nested || (nested.start === undefined && nested.end === undefined)) {
            return h;
        }
        // Flatten without losing the nested form — runtime callers that
        // already cope with `billboardRangeStart/End` keep working, and
        // anything still reading the nested form (e.g. legacy export paths)
        // sees the same data.
        return {
            ...h,
            billboardRangeStart: nested.start,
            billboardRangeEnd: nested.end,
        };
    });
    // Build ExportProps
    const exportProps: ExportProps = {
        // Scene Metadata
        name: scene.name || 'StorySplat Scene',
        sceneId: scene.sceneId,
        userId: scene.userId,
        userName: scene.userName || 'Unknown',
        thumbnailUrl: scene.thumbnailUrl,
        // Splat Configuration
        splatUrl,
        sogUrl,
        lodMetaUrl,
        // Mobile-specific URLs (picked at runtime on mobile UAs)
        mobileSplatUrl,
        mobileSogUrl,
        mobileLodMetaUrl,
        mobileCompressedPlyUrl,
        // Build fallback URLs from all available formats (excluding the primary)
        fallbackUrls: [
            ...(scene.fallbackUrls || []),
            // Add other available formats as fallbacks
            sogUrl !== splatUrl ? sogUrl : null,
            compressedPlyUrl !== splatUrl ? compressedPlyUrl : null,
            originalUrl !== splatUrl ? originalUrl : null
        ].filter((url): url is string => url != null && url !== ''),
        // Handle both splatScale (number) and scale (object) formats
        scale: scene.scale || (scene.splatScale != null
            ? { x: scene.splatScale, y: scene.splatScale, z: scene.splatScale }
            : { x: 1, y: 1, z: 1 }),
        // Handle both array and object position formats
        splatPosition: scene.splatPosition || scene.position || scene.cameraPosition || [0, 0, 0],
        splatRotation: scene.splatRotation || scene.rotation || scene.cameraRotation || [0, 0, 0],
        invertXScale: scene.invertXScale,
        invertYScale: scene.invertYScale,
        // Scene Data
        waypoints,
        // Named views off the tour path (FOV in degrees, unusable entries dropped)
        extraWaypoints: normalizeExtraWaypoints(scene.extraWaypoints),
        hotspots: resolvedHotspots,
        portals: scene.portals || [],
        overlays: scene.overlays || [],
        // Prefer nested skybox, but keep flat properties populated for compatibility.
        skybox: resolvedSkybox,
        skyboxUrl: resolvedSkybox?.url,
        skyboxRotation: resolvedSkybox?.rotation,
        skyboxVerticalOffset: resolvedSkybox?.verticalOffset,
        customMeshes: scene.customMeshes || [],
        htmlMeshes: scene.htmlMeshes || [],
        // Migrate legacy hemispheric lights to directional
        lights: (scene.lights || []).map((light: LightConfig) =>
            light.type === 'hemispheric'
                ? { ...light, type: 'directional' as const, direction: light.direction ?? { x: 0, y: -1, z: 0 } }
                : light
        ),
        particles: resolvedParticles,
        collisionMeshesData: scene.collisionMeshesData || [],
        voxelCollisionUrl: scene.voxelCollisionUrl,
        collisionMeshUrl: scene.collisionMeshUrl,
        collisionSystem: scene.collisionSystem,
        splatCollisionEnabled: scene.splatCollisionEnabled,
        playerHeight: scene.playerHeight,
        playerWidth: scene.playerWidth,
        walkSpeed: scene.walkSpeed,
        // UI Configuration
        uiColor: scene.uiColor || '#ffffff',
        uiOptions: {
            showStartExperience: scene.uiOptions?.showStartExperience ?? false,
            showWatermark: scene.uiOptions?.showWatermark ?? true,
            hideFullscreenButton: scene.uiOptions?.hideFullscreenButton ?? false,
            hideInfoButton: scene.uiOptions?.hideInfoButton ?? false,
            hideMuteButton: scene.uiOptions?.hideMuteButton ?? false,
            hideHelpButton: scene.uiOptions?.hideHelpButton ?? false,
            hideNavigator: scene.uiOptions?.hideNavigator ?? false,
            showWaypointList: scene.uiOptions?.showWaypointList ?? true,
            hideWatermark: scene.uiOptions?.hideWatermark ?? false,
            watermarkText: scene.uiOptions?.watermarkText,
            watermarkLink: scene.uiOptions?.watermarkLink,
            watermarkImageUrl: scene.uiOptions?.watermarkImageUrl,
            watermarkImageHeight: scene.uiOptions?.watermarkImageHeight,
            // Bottom-left scene title overlay (matches watermark style)
            showSceneTitle: scene.uiOptions?.showSceneTitle,
            sceneTitleShowOnHover: scene.uiOptions?.sceneTitleShowOnHover,
            movementKeyDisplay: scene.uiOptions?.movementKeyDisplay,
            buttonPosition: scene.uiOptions?.buttonPosition || 'inline',
            buttonLabels: scene.uiOptions?.buttonLabels,
            // Whitelabeling option for custom preloader logo
            customPreloaderLogoUrl: scene.uiOptions?.customPreloaderLogoUrl,
            preloaderLogoSize: scene.uiOptions?.preloaderLogoSize,
            // Lazy loading options
            lazyLoad: scene.uiOptions?.lazyLoad,
            lazyLoadButtonText: scene.uiOptions?.lazyLoadButtonText,
            lazyLoadThumbnailUrl: scene.uiOptions?.lazyLoadThumbnailUrl,
            lazyLoadThumbnailType: scene.uiOptions?.lazyLoadThumbnailType,
            // Layout type
            uiType: scene.uiOptions?.uiType,
            // Debug mode (FPS counter)
            debugMode: scene.uiOptions?.debugMode,
            // Hide progress text
            hideProgressText: scene.uiOptions?.hideProgressText,
            // Custom viewer theme
            viewerTheme: scene.uiOptions?.viewerTheme,
            // Custom external links for scene menu
            sceneMenuLinks: scene.uiOptions?.sceneMenuLinks,
            // Scene menu layout style (classic dropdown vs full sidebar pullout).
            // Must be carried through the transform or the real/published viewer never
            // sees it — the theme-editor preview bypasses this path, which is why the
            // sidebar style worked in the preview but not the actual viewer.
            sceneMenuStyle: scene.uiOptions?.sceneMenuStyle ?? 'classic',
            // Scene menu: start category dropdowns collapsed + optional title/logo at top
            startCategoriesCollapsed: scene.uiOptions?.startCategoriesCollapsed,
            menuTitle: scene.uiOptions?.menuTitle,
            menuTitleImageUrl: scene.uiOptions?.menuTitleImageUrl,
            // Scene-menu preview thumbnails + current-space slot + explicit folder order — MUST be
            // carried through this transform or the real/published viewer never sees them (the exact
            // same drop as sceneMenuStyle above: worked in the preview, not in the actual menu).
            showSceneMenuPreviewImages: scene.uiOptions?.showSceneMenuPreviewImages,
            currentSpaceMenuPath: scene.uiOptions?.currentSpaceMenuPath,
            currentSpaceMenuOrder: scene.uiOptions?.currentSpaceMenuOrder,
            sceneMenuFolderOrder: scene.uiOptions?.sceneMenuFolderOrder,
            preloadHotspotImages: scene.uiOptions?.preloadHotspotImages ?? false,
            // Measurement settings
            perfHudEnabled: scene.uiOptions?.perfHudEnabled,
            forceMobilePerformanceMode: scene.uiOptions?.forceMobilePerformanceMode,
            forceDesktopPerformanceMode: scene.uiOptions?.forceDesktopPerformanceMode,
            onDemandRendering: scene.uiOptions?.onDemandRendering,
            measurementsEnabled: scene.uiOptions?.measurementsEnabled,
            sceneScale: scene.uiOptions?.sceneScale,
            sceneScaleUnit: scene.uiOptions?.sceneScaleUnit,
            measurementColor: scene.uiOptions?.measurementColor,
            includeDefaultMeasurementsInViewer: scene.uiOptions?.includeDefaultMeasurementsInViewer,
            defaultMeasurementsVisibleOnLoad: scene.uiOptions?.defaultMeasurementsVisibleOnLoad,
            alwaysShowDefaultMeasurements: scene.uiOptions?.alwaysShowDefaultMeasurements,
            hideShareButton: scene.uiOptions?.hideShareButton,
            sharePhotoHideEntities: scene.uiOptions?.sharePhotoHideEntities,
            hideSceneMenu: scene.uiOptions?.hideSceneMenu,
            showFisheyeButton: scene.uiOptions?.showFisheyeButton,
            cutawayEnabled: scene.uiOptions?.cutawayEnabled,
            cutawayDirection: scene.uiOptions?.cutawayDirection,
            cutawayDisablesCollision: scene.uiOptions?.cutawayDisablesCollision,
            stereoEnabled: scene.uiOptions?.stereoEnabled,
            showStereoButton: scene.uiOptions?.showStereoButton,
            stereoDepth: scene.uiOptions?.stereoDepth,
            stereoScreenDistance: scene.uiOptions?.stereoScreenDistance,
            hidePhotoButton: scene.uiOptions?.hidePhotoButton,
            protectMedia: scene.uiOptions?.protectMedia,
            pauseOnHide: scene.uiOptions?.pauseOnHide,
            fpvKeyboardShortcutEnabled: scene.uiOptions?.fpvKeyboardShortcutEnabled,
            // "Ask this scene" visitor guide; resolved (enabled / scene id / editor) in sceneGuide.ts.
            sceneGuide: scene.uiOptions?.sceneGuide
        },
        // Editor-authored default measurements (locked in viewer)
        measurements: scene.measurements,
        areaMeasurements: scene.areaMeasurements,
        // Measurement sets: the groups visitors pick in the Measure panel (when the scene allows it) (setId on each measurement)
        measurementSets: scene.measurementSets,
        measurementSetsInLayersMenu: scene.measurementSetsInLayersMenu,
        measurementSetsStartHidden: scene.measurementSetsStartHidden,
        // Coloured overlays on the splat + the layers panel that switches them
        annotationLayers: scene.annotationLayers,
        // Regions (replace focus regions + annotation layers; those convert when this is absent)
        regions: scene.regions,
        hotspotFolders: scene.hotspotFolders,
        visitorDirectory: scene.visitorDirectory,
        // Camera Configuration
        defaultCameraMode: scene.defaultCameraMode || 'tour',
        allowedCameraModes: scene.allowedCameraModes || ['tour', 'explore'],
        cameraMovementSpeed: scene.cameraMovementSpeed,
        cameraRotationSensitivity: scene.cameraRotationSensitivity,
        cameraDamping: scene.cameraDamping,
        invertCameraRotation: scene.invertCameraRotation,
        // Scene-level FOV (convert from radians if needed, same logic as per-waypoint)
        fov: sceneFov,
        // Navigation Configuration
        includeScrollControls: scene.includeScrollControls ?? true,
        scrollButtonMode: scene.scrollButtonMode || 'continuous',
        scrollAmount: scene.scrollAmount || 100,
        scrollSpeed: scene.scrollSpeed,
        transitionSpeed: scene.transitionSpeed,
        waypointEasing: scene.waypointEasing,
        pathInterpolation: scene.pathInterpolation,
        autoPlayEnabled: scene.autoPlayEnabled ?? false,
        autoPlayWaitForFullDetail: scene.autoPlayWaitForFullDetail ?? false,
        // Playback settings
        autoplaySpeed: scene.autoplaySpeed ?? scene.autoPlaySpeed,
        loopMode: scene.loopMode,
        // XR Configuration
        includeXR: scene.includeXR,
        xrMode: scene.xrMode === 'ar' || scene.xrMode === 'vr' || scene.xrMode === 'both' ? scene.xrMode : undefined,
        eighthWallBaseUrl: sameSiteEighthWallBaseUrl(scene.eighthWallBaseUrl),
        // Custom Script
        customScript: scene.customScript,
        // Background color (rendered behind splat; read by createViewer.ts).
        backgroundColor: scene.backgroundColor,
        // Camera clip planes — mapped to near/far in exportPropsToViewerConfig.
        minClipPlane: scene.minClipPlane,
        maxClipPlane: scene.maxClipPlane,
        // Template type (read from scene uiOptions, default to minimal)
        templateType: (scene.uiOptions?.uiType as TemplateType) || 'minimal',
        // Splat Swap System
        additionalSplats: scene.additionalSplats || [],
        keepMeshesInMemory: scene.keepMeshesInMemory ?? false,
        enabledExploreModes: scene.enabledExploreModes,
        initialSplatExploreMode: scene.initialSplatExploreMode,
        // Audio emitters
        audioEmitters: scene.audioEmitters || [],
        // Double-tap auto-move speed
        doubleTapMoveSpeed: scene.doubleTapMoveSpeed,
        // Head bob toggle
        headBobEnabled: scene.headBobEnabled,
        // 4DGS frame sequence
        frameSequence: scene.frameSequence,
        // Splat relighting
        splatRelighting: scene.splatRelighting,
        // Reveal effect: prefer explicit revealEffect, fall back to legacy useNodeMaterial toggle
        // (useNodeMaterial: false means user disabled the fade → 'none', true/undefined → 'medium')
        revealEffect: scene.revealEffect ?? (scene.useNodeMaterial === false ? 'none' : 'medium'),
        revealStyle: scene.revealStyle,
        // Swap transition type
        swapTransitionType: scene.swapTransitionType,
        // LOD settings
        lodSettings: scene.lodSettings,
        mobileLodSettings: scene.mobileLodSettings,
        // Orbit camera settings
        orbitCameraSettings: scene.orbitCameraSettings,
        // Walk camera settings
        walkCameraSettings: scene.walkCameraSettings,
        walkSpawnOverride: scene.walkSpawnOverride,
        // Refocus tap mode
        refocusTapMode: scene.refocusTapMode,
        // Swap transition toggle
        enableSwapTransition: scene.enableSwapTransition,
        // Compare mode
        splatSwapMode: scene.splatSwapMode,
        compareSettings: scene.compareSettings,
        // Entity animations
        entityAnimations: scene.entityAnimations || [],
        // Post-processing
        postProcessing: scene.postProcessing,
        // Mirror planes
        mirrorPlanes: scene.mirrorPlanes || [],
        // Water planes render unless the scene turns water off explicitly (enableWaterEffects: false).
        // Scenes saved while water was disabled in the editor carry no waterPlanes at all.
        enableWaterEffects: scene.enableWaterEffects !== false,
        waterPlanes: scene.enableWaterEffects !== false ? scene.waterPlanes || [] : [],
        // Procedural cloud layers: generated in the viewer from their seed (nothing to package).
        cloudLayers: Array.isArray(scene.cloudLayers) ? scene.cloudLayers : [],
        scanSky: scene.scanSky,
        // Procedural splat weather (2.18+)
        weather: scene.weather,
        // Scene wind: wind-reactive objects and areas, clouds that follow it
        wind: scene.wind,
        // Audio-reactive splat effect
        audioReactive: scene.audioReactive,
        // Segment data
        segmentDataUrl: scene.segmentDataUrl,
        segmentMetaUrl: scene.segmentMetaUrl,
        enableSegmentHover: scene.enableSegmentHover,
        // Narration track
        narrationTrack: scene.narrationTrack,
        // Cross-scene ambient audio bed (persists across portal teleports)
        crossSceneAudio: scene.crossSceneAudio,
        // AI re-skins (viewer shows a palette dropdown in the top-left toolbar)
        skins: scene.skins,
    };
    return exportProps;
}
/**
 * Viewer configuration for dynamic viewer runtime
 */
export interface ViewerConfig {
    splatUrl: string;
    sogUrl?: string;
    /** LOD streaming format URL (lod-meta.json) - highest priority for loading */
    lodMetaUrl?: string;
    /** Optional mobile-only counterparts. The viewer picks these on mobile UAs (or when forceMobilePreview is true). */
    mobileSplatUrl?: string;
    mobileSogUrl?: string;
    mobileLodMetaUrl?: string;
    mobileCompressedPlyUrl?: string;
    /** Editor-only override: forces the viewer to treat this session as a mobile UA. */
    forceMobilePreview?: boolean;
    fallbackUrls?: string[];
    scale?: {
        x: number;
        y: number;
        z: number;
    };
    position: number[];
    rotation: number[];
    invertXScale?: boolean;
    invertYScale?: boolean;
    waypoints?: WaypointData[];
    /** Named camera views off the tour path (hotspot "Fly to extra waypoint", goToExtraWaypoint). */
    extraWaypoints?: ExtraWaypointData[];
    hotspots?: HotspotData[];
    portals?: PortalData[];
    overlays?: OverlayData[];
    /** Nested skybox config (newer format) */
    skybox?: {
        url: string;
        rotation?: number;
        /** Horizon offset in degrees — reprojects the skybox so its horizon shifts up (+) or down (-) relative to the scene. */
        verticalOffset?: number;
        intensity?: number;
        enableIBL?: boolean;
    };
    /** Flat skybox URL (older format, for backwards compatibility) */
    skyboxUrl?: string;
    /** Flat skybox rotation in radians (older format, for backwards compatibility) */
    skyboxRotation?: number;
    /** Flat skybox horizon offset in degrees (older format, for backwards compatibility) */
    skyboxVerticalOffset?: number;
    customMeshes?: CustomMeshConfig[];
    htmlMeshes?: HtmlMeshConfig[];
    lights?: LightConfig[];
    particles?: ParticleConfig[];
    collisionMeshesData?: CollisionMeshConfig[];
    /** Voxel octree collision (.voxel.json URL). .voxel.bin is co-located. */
    voxelCollisionUrl?: string;
    /** Triangle collision mesh (.collision.glb URL). */
    collisionMeshUrl?: string;
    /** Runtime collision system selector (default: 'voxel'). */
    collisionSystem?: 'voxel' | 'mesh';
    /** Master enable for splat-derived collision (default: true). */
    splatCollisionEnabled?: boolean;
    cameraMode?: string;
    defaultCameraMode?: string;
    allowedCameraModes?: string[];
    autoPlay?: boolean;
    /** Hold the scene's initial autoplay until LOD streaming has settled (published viewer only). */
    autoPlayWaitForFullDetail?: boolean;
    uiColor?: string;
    uiOptions?: SceneData['uiOptions'];
    // Camera settings (matching HTML export CONFIG)
    fov?: number;
    nearClip?: number;
    farClip?: number;
    playerHeight?: number;
    /** Walk-mode player body width (collision diameter, scene units). Default 0.6. */
    playerWidth?: number;
    walkSpeed?: number;
    cameraMovementSpeed?: number;
    cameraRotationSensitivity?: number;
    cameraDamping?: number;
    invertCameraRotation?: boolean;
    // Navigation settings
    scrollSpeed?: number;
    scrollAmount?: number;
    scrollButtonMode?: 'continuous' | 'incremental' | 'percentage' | 'waypoint';
    transitionSpeed?: number;
    waypointEasing?: import('../types').WaypointEasingMode;
    pathInterpolation?: import('../utils/waypointPath').WaypointInterpolation;
    // Playback settings
    /** Speed of autoplay in progress per second (default: calculated from waypoint durations) */
    autoplaySpeed?: number;
    /** Loop behavior for playback: 'loop' restarts at beginning, 'pingpong' reverses direction, 'none' stops at end */
    loopMode?: 'loop' | 'pingpong' | 'none';
    // Splat Swap System
    additionalSplats?: SplatSwapPoint[];
    keepMeshesInMemory?: boolean;
    /** Explore sub-modes available inside Explore mode. Missing means both orbit and fly are available. 'walk' requires collision data. */
    enabledExploreModes?: Array<'orbit' | 'fly' | 'walk'>;
    /** Default explore sub-mode for the initial splat. 'orbit' for aerial views, 'fly' for interior POV, 'walk' for first-person. */
    initialSplatExploreMode?: 'orbit' | 'fly' | 'walk';
    /** Speed of auto-forward movement triggered by double-tap in explore mode (default: 1.0) */
    doubleTapMoveSpeed?: number;
    /** Whether head bob is enabled in walk mode (default: true) */
    headBobEnabled?: boolean;
    // XR Configuration
    includeXR?: boolean;
    xrMode?: 'ar' | 'vr' | 'both';
    eighthWallBaseUrl?: string;
    // Custom Script
    customScript?: string;
    // Background color
    backgroundColor?: string;
    // Audio emitters
    audioEmitters?: AudioEmitterConfig[];
    // 4DGS frame sequence
    frameSequence?: {
        frameUrls: string[];
        fps?: number;
        loop?: boolean;
        preloadCount?: number;
        autoplay?: boolean;
    };
    // Splat relighting
    splatRelighting?: import('../types').SplatRelightingConfig;
    // Reveal effect
    revealEffect?: 'fast' | 'medium' | 'slow' | 'none';
    /** Reveal effect style: 'bloom' (fast burst with overshoot) or 'radial' (classic dot wave). Default: 'bloom' */
    revealStyle?: 'bloom' | 'radial';
    // Swap transition type
    swapTransitionType?: 'scanline' | 'dissolve';
    // LOD settings
    lodSettings?: import('../types').LodSettingsConfig;
    mobileLodSettings?: import('../types').LodSettingsConfig;
    /** Initial orbit camera settings (position + pivot) */
    orbitCameraSettings?: OrbitCameraSettingsConfig;
    /** Initial walk mode camera settings (position + euler rotation in degrees, PlayCanvas coords) */
    walkCameraSettings?: { cameraPosition: { x: number; y: number; z: number }; rotation: { x: number; y: number; z: number } };
    /** When true, entering walk mode teleports the camera to walkCameraSettings. Default: false. */
    walkSpawnOverride?: boolean;
    /** Whether camera refocus triggers on single tap or double tap (default: 'single') */
    refocusTapMode?: 'single' | 'double';
    /** Whether dissolve/wipe transition is enabled for splat swaps (default: true) */
    enableSwapTransition?: boolean;
    /** Splat swap mode: 'chain' (sequential), 'compare' (before/after slider), or 'menu' (no auto-trigger; swapped only from scene-menu items). Default: 'chain' */
    splatSwapMode?: 'chain' | 'compare' | 'menu';
    /** Compare mode settings (only used when splatSwapMode === 'compare') */
    compareSettings?: import('../types').CompareConfig;
    /** Entity keyframe animations */
    entityAnimations?: import('../types').EntityAnimationConfig[];
    /** Post-processing settings */
    postProcessing?: import('../types').PostProcessingConfig;
    /** Mirror reflection planes */
    mirrorPlanes?: import('../types').MirrorPlaneData[];
    /** Feature flag for water surface effects. Water planes are ignored unless true. */
    enableWaterEffects?: boolean;
    /** Water surface planes for broken pool/pond regions */
    waterPlanes?: import('../types').WaterPlaneData[];
    /** Procedural cloud layers */
    cloudLayers?: import('../types').CloudLayerData[];
    /** The scan's baked sky: keep or replace */
    scanSky?: import('../types').ScanSkySettings;
    /** Procedural splat weather (PlayCanvas 2.18+) */
    weather?: import('../types').WeatherConfig;
    /** Scene wind (effects/wind) */
    wind?: import('../types').SceneWindSettings;
    /** Audio-reactive splat effect (music-driven motion + color) */
    audioReactive?: import('../types').AudioReactiveConfig;
    /** Segment data binary URL (.segments.bin - Uint16 per Gaussian) */
    segmentDataUrl?: string;
    /** Segment metadata JSON URL (.segments.json) */
    segmentMetaUrl?: string;
    /** Enable segment hover highlighting in viewer */
    enableSegmentHover?: boolean;
    /** Guided narration track synced to tour progress */
    narrationTrack?: import('../types').NarrationTrackConfig;
    /** Background music bed that persists across portal teleports */
    crossSceneAudio?: import('../types').CrossSceneAudioConfig;
    /** AI re-skin images tied to camera poses (v2 viewer UI) */
    skins?: import('../types').SceneSkinExport[];
    /** Editor-authored measurements shipped as locked defaults in the viewer. */
    measurements?: import('../types').MeasurementData[];
    /** Editor-authored area measurements shipped as locked defaults in the viewer. */
    areaMeasurements?: import('../types').AreaMeasurementData[];
    /** Named groups of the saved measurements; visitors pick them in the Measure panel when the scene allows it. */
    measurementSets?: import('../types').MeasurementSetData[];
    /** Visitors pick measurement sets (in the Measure panel; the name is historical). */
    measurementSetsInLayersMenu?: boolean;
    /** Those sets start unticked. */
    measurementSetsStartHidden?: boolean;
    /** Coloured overlays drawn on the splat, switched from the layers panel. */
    annotationLayers?: import('../effects/annotation-layers').AnnotationLayersConfig;
    /** Regions: colour, spotlight or label parts of the scan (read instead of focusRegions + annotationLayers when present). */
    regions?: import('../effects/regions').RegionsConfig;
    /** Hotspot folders in order (hotspots name theirs with folderId). */
    hotspotFolders?: import('../dynamic-viewer/hotspotFolders').HotspotFoldersConfig;
    /** The visitor directory (hotspots by folder and the extra waypoints). Absent: off. */
    visitorDirectory?: import('../dynamic-viewer/hotspotFolders').VisitorDirectoryConfig;
}
/**
 * Transform ExportProps to scene config for dynamic viewer
 * This is a simpler format for runtime use
 */
export function exportPropsToViewerConfig(props: ExportProps): ViewerConfig {
    // Use scene-level FOV, falling back to first waypoint FOV, then default 60
    const fov = props.fov || props.waypoints?.[0]?.fov || 60;
    return {
        splatUrl: props.splatUrl,
        sogUrl: props.sogUrl,
        lodMetaUrl: props.lodMetaUrl,
        mobileSplatUrl: props.mobileSplatUrl,
        mobileSogUrl: props.mobileSogUrl,
        mobileLodMetaUrl: props.mobileLodMetaUrl,
        mobileCompressedPlyUrl: props.mobileCompressedPlyUrl,
        fallbackUrls: props.fallbackUrls,
        scale: props.scale,
        position: props.splatPosition || [0, 0, 0],
        rotation: props.splatRotation || [0, 0, 0],
        invertXScale: props.invertXScale,
        invertYScale: props.invertYScale,
        waypoints: props.waypoints,
        extraWaypoints: props.extraWaypoints,
        hotspots: props.hotspots,
        portals: props.portals,
        overlays: props.overlays,
        skybox: props.skybox,
        skyboxUrl: props.skyboxUrl,
        skyboxRotation: props.skyboxRotation,
        skyboxVerticalOffset: props.skyboxVerticalOffset,
        customMeshes: props.customMeshes,
        htmlMeshes: props.htmlMeshes,
        lights: props.lights,
        particles: props.particles,
        collisionMeshesData: props.collisionMeshesData,
        voxelCollisionUrl: props.voxelCollisionUrl,
        collisionMeshUrl: props.collisionMeshUrl,
        collisionSystem: props.collisionSystem,
        splatCollisionEnabled: props.splatCollisionEnabled,
        cameraMode: props.defaultCameraMode,
        defaultCameraMode: props.defaultCameraMode,
        allowedCameraModes: props.allowedCameraModes,
        autoPlay: props.autoPlayEnabled,
        autoPlayWaitForFullDetail: props.autoPlayWaitForFullDetail,
        uiColor: props.uiColor,
        uiOptions: props.uiOptions,
        // Camera settings (matching HTML export CONFIG)
        fov: fov,
        nearClip: props.minClipPlane || 0.1,
        farClip: props.maxClipPlane || 1000,
        playerHeight: props.playerHeight || 1.8,
        playerWidth: props.playerWidth,
        walkSpeed: props.walkSpeed,
        cameraMovementSpeed: props.cameraMovementSpeed || 1,
        cameraRotationSensitivity: props.cameraRotationSensitivity || 0.2,
        cameraDamping: props.cameraDamping ?? 0.75,
        invertCameraRotation: props.invertCameraRotation,
        // Navigation settings
        scrollSpeed: props.scrollSpeed,
        scrollAmount: props.scrollAmount,
        scrollButtonMode: props.scrollButtonMode,
        transitionSpeed: props.transitionSpeed,
        waypointEasing: props.waypointEasing,
        pathInterpolation: props.pathInterpolation,
        // Playback settings
        autoplaySpeed: props.autoplaySpeed,
        loopMode: props.loopMode,
        // Splat Swap System
        additionalSplats: props.additionalSplats,
        keepMeshesInMemory: props.keepMeshesInMemory ?? false,
        enabledExploreModes: props.enabledExploreModes,
        initialSplatExploreMode: props.initialSplatExploreMode,
        // XR Configuration
        includeXR: props.includeXR,
        xrMode: props.xrMode,
        eighthWallBaseUrl: props.eighthWallBaseUrl,
        // Custom Script
        customScript: props.customScript,
        // Background color (consumed by createViewer to tint the canvas behind the splat).
        backgroundColor: props.backgroundColor,
        // Audio emitters
        audioEmitters: props.audioEmitters || [],
        // Double-tap auto-move speed
        doubleTapMoveSpeed: props.doubleTapMoveSpeed,
        // Head bob toggle
        headBobEnabled: props.headBobEnabled,
        // 4DGS frame sequence
        frameSequence: props.frameSequence,
        // Splat relighting
        splatRelighting: props.splatRelighting,
        // Reveal effect
        revealEffect: props.revealEffect,
        revealStyle: props.revealStyle,
        // Swap transition type
        swapTransitionType: props.swapTransitionType,
        // LOD settings
        lodSettings: props.lodSettings,
        mobileLodSettings: props.mobileLodSettings,
        // Orbit camera settings
        orbitCameraSettings: props.orbitCameraSettings,
        // Walk camera settings
        walkCameraSettings: props.walkCameraSettings,
        walkSpawnOverride: props.walkSpawnOverride,
        // Refocus tap mode
        refocusTapMode: props.refocusTapMode,
        // Swap transition toggle
        enableSwapTransition: props.enableSwapTransition,
        // Compare mode
        splatSwapMode: props.splatSwapMode,
        compareSettings: props.compareSettings,
        // Entity animations
        entityAnimations: props.entityAnimations || [],
        // Post-processing
        postProcessing: props.postProcessing,
        // Mirror planes
        mirrorPlanes: props.mirrorPlanes || [],
        // Water planes render unless the scene turns water off explicitly (enableWaterEffects: false).
        // Scenes saved while water was disabled in the editor carry no waterPlanes at all.
        enableWaterEffects: props.enableWaterEffects !== false,
        waterPlanes: props.enableWaterEffects !== false ? props.waterPlanes || [] : [],
        cloudLayers: Array.isArray(props.cloudLayers) ? props.cloudLayers : [],
        scanSky: props.scanSky,
        // Procedural splat weather (2.18+)
        weather: props.weather,
        // Scene wind: wind-reactive objects and areas, clouds that follow it
        wind: props.wind,
        // Audio-reactive splat effect
        audioReactive: props.audioReactive,
        // Segment data
        segmentDataUrl: props.segmentDataUrl,
        segmentMetaUrl: props.segmentMetaUrl,
        enableSegmentHover: props.enableSegmentHover,
        // Narration track
        narrationTrack: props.narrationTrack,
        // Cross-scene ambient audio bed
        crossSceneAudio: props.crossSceneAudio,
        // AI re-skins (viewer renders a palette-icon dropdown with overlay)
        skins: props.skins,
        // Editor-authored default measurements (rendered as locked in the viewer)
        measurements: props.measurements,
        areaMeasurements: props.areaMeasurements,
        measurementSets: props.measurementSets,
        measurementSetsInLayersMenu: props.measurementSetsInLayersMenu,
        measurementSetsStartHidden: props.measurementSetsStartHidden,
        annotationLayers: props.annotationLayers,
        regions: props.regions,
        hotspotFolders: props.hotspotFolders,
        visitorDirectory: props.visitorDirectory,
    };
}
